<div class="carbon-footprint-container">
  <header class="carbon-footprint-heading">
    <h1>{{ 'sustainability.carbon-footprint' | translate }}</h1>
  </header>
  <agr-extended-notification
    notificationType="info"
    [title]="'sustainability.notification-title' | translate"
    [actionText]="getActionText()"
    (action)="toggleNotification()"
  >
    @if (showNotification) {
      <div
        class="text--small"
        [innerHTML]="'sustainability.notification-description' | translate"
      ></div>
    }
  </agr-extended-notification>

  <div class="carbon-footprint-workspace">
    <section class="calculation-setup surface" aria-labelledby="carbon-footprint-setup-title">
      <h2 id="carbon-footprint-setup-title">{{ 'sustainability.calculations.new' | translate }}</h2>
      <mat-form-field class="item-selector">
        <mat-label>{{ 'cycle-overview.placeholder' | translate }}</mat-label>
        <mat-select
          [hideSingleSelectionIndicator]="true"
          [value]="selectedCycle"
          [disabled]="calculating()"
          data-test="cycle-overview__cycle-selector1"
          (selectionChange)="onCycleSelected($event.value)"
        >
          <mat-option *ngFor="let cycle of cycles$ | async" [value]="cycle">
            {{ 'cycle' | translate }} {{ cycle.cycleNumber }} - {{ cycle.concept?.name }}
          </mat-option>
        </mat-select>
      </mat-form-field>
      @if (selectedCycle) {
        <p class="cycle-period">
          {{ selectedCycle.cycleDate | date: 'shortDate' }}
          @if (selectedCycle.cycleEndDate) {
            - {{ selectedCycle.cycleEndDate | date: 'shortDate' }}
          }
        </p>
      }
      @if (calculationFailed()) {
        <div class="error-messages" role="alert" data-test="carbon-footprint-attempt-error">
          {{ 'sustainability.calculations.attempt-failed' | translate }}
        </div>
      }
      @if (selectedCycle) {
        <section
          class="readiness"
          aria-labelledby="carbon-footprint-readiness-title"
          [attr.aria-busy]="checkingReadiness()"
          data-test="carbon-footprint-readiness"
        >
          <h2 id="carbon-footprint-readiness-title">
            {{ 'sustainability.readiness.title' | translate }}
          </h2>
          <output id="carbon-footprint-readiness-summary" aria-live="polite">
            @if (checkingReadiness()) {
              {{ 'sustainability.readiness.checking' | translate }}
            } @else if (readinessError()) {
              {{ 'sustainability.readiness.error' | translate }}
            } @else if (canCalculate()) {
              {{ 'sustainability.readiness.ready' | translate }}
            } @else {
              {{ 'sustainability.readiness.blocked' | translate }}
            }
          </output>
          @if (readiness()) {
            <div class="readiness-progress">
              <p id="carbon-footprint-progress-label">
                {{ completedRequirements() }} / {{ readinessChecks().length }}
                {{ 'sustainability.readiness.requirements-met' | translate }}
              </p>
              <mat-progress-bar
                mode="determinate"
                [value]="
                  readinessChecks().length
                    ? (completedRequirements() / readinessChecks().length) * 100
                    : 0
                "
                aria-labelledby="carbon-footprint-progress-label"
                data-test="carbon-footprint-readiness-progress"
              ></mat-progress-bar>
            </div>
            <ul class="readiness-checks">
              @for (check of readinessChecks(); track check.requirement) {
                <li class="readiness-check" [class.readiness-check--blocked]="!check.complete">
                  <div class="readiness-heading">
                    <strong>{{
                      'sustainability.readiness.requirements.' + check.requirement | translate
                    }}</strong>
                    <span class="readiness-status">
                      {{
                        (check.complete
                          ? 'sustainability.readiness.complete'
                          : 'sustainability.readiness.action-required'
                        ) | translate
                      }}
                    </span>
                  </div>
                  <ng-template #issueDetails>
                    <ul class="readiness-issues">
                      @for (issue of check.issues; track $index) {
                        <li>
                          @if (issue.productTitle || issue.productIdentifier) {
                            <strong>{{ issue.productTitle || issue.productIdentifier }}</strong>
                            @if (issue.productTitle && issue.productIdentifier) {
                              <span> {{ issue.productIdentifier }}</span>
                            }
                          }
                          @if (issue.deliveryDate) {
                            <span> {{ issue.deliveryDate | date: 'shortDate' }}</span>
                          }
                          <div>{{ issueMessage(issue) }}</div>
                          @if (issue.averageArrivalWeightKg != null) {
                            <div>
                              {{ 'sustainability.readiness.arrival-weight' | translate }}:
                              {{ issue.averageArrivalWeightKg | number: '1.3-3' }} kg
                            </div>
                          }
                          @if (issue.averageDepartureWeightKg != null) {
                            <div>
                              {{ 'sustainability.readiness.departure-weight' | translate }}:
                              {{ issue.averageDepartureWeightKg | number: '1.3-3' }} kg
                            </div>
                          }
                          @if (issue.fields?.length) {
                            <ul>
                              @for (field of issue.fields; track field) {
                                <li>{{ fieldLabel(field) }}</li>
                              }
                            </ul>
                          }
                        </li>
                      }
                    </ul>
                  </ng-template>
                  @if (check.requirement === 'feeds' && check.issues.length) {
                    <details
                      class="readiness-feed-details"
                      data-test="carbon-footprint-feed-details"
                    >
                      <summary>
                        <span>{{ 'sustainability.readiness.view-details' | translate }}</span>
                        <span class="readiness-issue-count">({{ check.issues.length }})</span>
                      </summary>
                      <ng-container [ngTemplateOutlet]="issueDetails"></ng-container>
                    </details>
                  } @else {
                    <ng-container [ngTemplateOutlet]="issueDetails"></ng-container>
                  }
                  @if (!check.complete) {
                    @switch (check.requirement) {
                      @case ('cycle') {
                        <a [routerLink]="['/cycle', selectedCycle.id]">{{
                          'sustainability.readiness.edit-cycle' | translate
                        }}</a>
                        <a [routerLink]="['/cycle', selectedCycle.id, 'day-old-chick']">{{
                          'sustainability.readiness.edit-arrivals' | translate
                        }}</a>
                        <a [routerLink]="['/cycle', selectedCycle.id, 'departure']">{{
                          'sustainability.readiness.edit-departures' | translate
                        }}</a>
                      }
                      @case ('farm-resources') {
                        <a routerLink="/farm-resources">{{
                          'sustainability.readiness.edit-resources' | translate
                        }}</a>
                      }
                      @case ('bedding') {
                        <a [routerLink]="['/cycle', selectedCycle.id]">{{
                          'sustainability.readiness.edit-bedding' | translate
                        }}</a>
                      }
                      @case ('feeds') {
                        <a [routerLink]="['/cycle', selectedCycle.id, 'balance']">{{
                          'sustainability.readiness.edit-feeds' | translate
                        }}</a>
                        <p class="readiness-advice">
                          {{ 'sustainability.readiness.advisor' | translate }}
                        </p>
                      }
                    }
                  }
                </li>
              }
            </ul>
            <p class="text--small">{{ 'sustainability.readiness.disclaimer' | translate }}</p>
          }
          <div class="readiness-actions">
            <button
              mat-flat-button
              color="primary"
              [disabled]="!canCalculate()"
              aria-describedby="carbon-footprint-readiness-summary"
              (click)="startCalculateCarbonFootprint()"
              data-test="cycle-overview__start-calculate-carbon-footprint-button"
            >
              {{ 'sustainability.start-calculation' | translate }}
            </button>
          </div>
        </section>
      }

      @if (calculating()) {
        <output>{{ 'sustainability.calculations.calculating' | translate }}</output>
        <mat-progress-bar
          mode="indeterminate"
          [attr.aria-label]="'sustainability.calculations.calculating' | translate"
        ></mat-progress-bar>
      }
    </section>

    <div class="calculation-results">
      <section
        class="surface result-panel"
        [class.result-panel--populated]="calculations().length > 0"
        aria-labelledby="carbon-footprint-result-title"
        [attr.aria-busy]="loadingResults()"
      >
        <div class="result-heading">
          <h2 id="carbon-footprint-result-title">
            {{ 'sustainability.calculations.result' | translate }}
          </h2>
          <button
            type="button"
            mat-flat-button
            color="primary"
            [disabled]="!selectedCycle || calculating() || loadingResults()"
            (click)="refreshCarbonFootprintResult()"
            data-test="cycle-overview__refresh-carbon-footprint-result-button"
          >
            <mat-icon>refresh</mat-icon>{{ 'sustainability.refresh-carbon-footprint' | translate }}
          </button>
        </div>
        @if (loadingResults()) {
          <output>{{ 'sustainability.calculations.loading' | translate }}</output>
          <mat-progress-bar
            mode="indeterminate"
            [attr.aria-label]="'sustainability.calculations.loading' | translate"
          ></mat-progress-bar>
        }
        @if (resultsError()) {
          <div class="error-messages" role="alert">
            <p>{{ 'sustainability.calculations.load-error' | translate }}</p>
            <button type="button" mat-stroked-button (click)="loadResults()">
              {{ 'sustainability.calculations.retry' | translate }}
            </button>
          </div>
        }
        @if (selectedCalculation(); as result) {
          <div class="result-context" data-test="carbon-footprint-result-context">
            <h3>
              {{ 'cycle' | translate }} {{ selectedCycle.cycleNumber }} ·
              {{ selectedCycle.concept?.name }}
            </h3>
            <span
              >{{ 'sustainability.calculations.calculation' | translate }}
              {{ calculations().length - calculations().indexOf(result) }}</span
            >
            <span
              class="result-status"
              [class.result-status--failed]="resultStatus(result) === 'failed'"
            >
              {{ 'sustainability.calculations.status.' + resultStatus(result) | translate }}
            </span>
            @if (result.updated) {
              <time
                class="result-date"
                [attr.datetime]="result.updated"
                data-test="carbon-footprint-result-updated"
                >{{ result.updated | date: 'short' : undefined : (currentLocale$ | async) }}</time
              >
            }
          </div>
          @switch (resultStatus(result)) {
            @case ('complete') {
              @if (climateImpact(result); as impact) {
                <div class="footprint-total">
                  <span>{{ impact.name }}</span>
                  <div>
                    <strong>{{ impact.value | number: '1.3-3' }}</strong
                    ><span>{{ impact.unit }}</span>
                  </div>
                </div>
              }
              <agr-table
                [config]="tableConfig"
                [emptyTitle]="''"
                [emptyDescription]="''"
                data-test="carbon-footprint-result-table"
              ></agr-table>
            }
            @case ('failed') {
              <div class="error-messages" role="alert">
                @if (carbonFootprintErrors.length) {
                  <p>{{ 'sustainability.carbon-footprint-title-error' | translate }}</p>
                  <ul>
                    @for (message of carbonFootprintErrors; track $index) {
                      <li>{{ message }}</li>
                    }
                  </ul>
                } @else {
                  <p>{{ 'sustainability.calculations.result-failed' | translate }}</p>
                }
              </div>
            }
            @case ('pending') {
              <output>{{ 'sustainability.calculations.pending' | translate }}</output>
            }
          }
        } @else if (!loadingResults() && !resultsError()) {
          <p class="empty-result" data-test="carbon-footprint-empty-result">
            {{
              (selectedCycle
                ? 'sustainability.calculations.empty'
                : 'sustainability.calculations.select-cycle'
              ) | translate
            }}
          </p>
        }
      </section>

      <section class="surface history-panel" aria-labelledby="carbon-footprint-history-title">
        <div class="result-heading">
          <h2 id="carbon-footprint-history-title">
            {{ 'sustainability.calculations.history' | translate }}
          </h2>
        </div>
        @if (selectedCycle) {
          <p>
            {{ 'sustainability.calculations.history-description' | translate }}
          </p>
        }
        <div class="history-cards">
          @for (result of calculations(); track result.id; let index = $index) {
            <button
              type="button"
              class="history-card"
              [class.history-card--selected]="selectedCalculationId() === result.id"
              [attr.aria-pressed]="selectedCalculationId() === result.id"
              (click)="selectCalculation(result.id)"
              data-test="carbon-footprint-history-item"
            >
              <strong
                >{{ 'sustainability.calculations.calculation' | translate }}
                {{ calculations().length - index }}</strong
              >
              @if (result.updated) {
                <time
                  class="history-date"
                  data-test="carbon-footprint-history-updated"
                  [attr.datetime]="result.updated"
                  >{{ result.updated | date: 'short' : undefined : (currentLocale$ | async) }}</time
                >
              }
              <span
                class="result-status history-status"
                [class.result-status--failed]="resultStatus(result) === 'failed'"
                >{{
                  'sustainability.calculations.status.' + resultStatus(result) | translate
                }}</span
              >
              @if (resultStatus(result) === 'complete') {
                @if (climateImpact(result); as impact) {
                  <span class="history-impact"
                    ><b>{{ impact.value | number: '1.3-3' }}</b
                    ><span>{{ impact.unit }}</span></span
                  >
                }
              }
            </button>
          } @empty {
            <p>
              {{
                (selectedCycle
                  ? 'sustainability.calculations.empty'
                  : 'sustainability.calculations.select-cycle'
                ) | translate
              }}
            </p>
          }
        </div>
      </section>
    </div>
  </div>
  <footer class="carbon-footprint-footer">
    <span>Powered by</span>
    @if (sustellLogoSrc !== null) {
      <img class="sustell-logo" [src]="sustellLogoSrc" alt="Sustell" />
    }
  </footer>
</div>
